import { Inject } from '@odinjs/odin';
import { ButtonAPI, FetchAPI, FieldAPI, FormAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnChange, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component';
import { SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, Validator } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PseudonymAppointmentsCancelAliases } from '../../../../codegen';
import { APPLICATION_ENDPOINTS } from '../../../../constants/application-endpoints';
import { SCHEDULE_OTHER_STATUS } from '../../../../constants/schedule-status';
import PseudonymAppointmentsBaseController from '../../pseudonym-appointments-base-controller';

interface AppointmentCancelForm {
  reason: CancelReason;
  reasonCancellation: string;
}

interface CancelReason {
  cdMotivo: string;
  dsMotivo: string;
}

@Controller({ component: PseudonymAppointmentsCancelAliases.LAYOUT_CANCEL, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_CANCEL })
export default class AppointmentCancelController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsCancelAliases.PANEL_FORM)
    panelFormApi!: PanelAPI;

  @Inject(PseudonymAppointmentsCancelAliases.PANEL_ACTION)
    panelActionApi!: PanelAPI;

  @Inject(PseudonymAppointmentsCancelAliases.FORM_CANCEL)
    formCancelApi!: FormAPI;

  @Inject(PseudonymAppointmentsCancelAliases.FETCH_CANCEL_REASON_LIST)
    featchCancelReasonListApi!: FetchAPI;

  @Inject(PseudonymAppointmentsCancelAliases.FIELD_CANCEL_REASON_LIST)
    fieldCancelReasonListApi!: FieldAPI;

  @Inject(PseudonymAppointmentsCancelAliases.FIELD_CANCEL_REASON)
    fieldCancelReasonApi!: FieldAPI;

  @Inject(PseudonymAppointmentsCancelAliases.BUTTON_CONFIRM_CANCELLATION)
    buttonConfirmCancellationApi!: ButtonAPI;

  @Inject(PseudonymAppointmentsCancelAliases.PANEL_LOADER)
    panelLoaderApi!: PanelAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @OnMount({ target: PseudonymAppointmentsCancelAliases.LAYOUT_CANCEL })
  handleOnMountLayoutCancel(): void {
    this.panelLoaderApi.setVisible(false);
  }

  @OnMount({ target: PseudonymAppointmentsCancelAliases.FORM_CANCEL })
  handleOnMountFormCancel(): void {
    const isAppointmentRequest = this.context.get('scheduleType') === SCHEDULE_OTHER_STATUS.REQUESTED;
    const requiredValidator: Validator = { name: 'required', options: {} };

    this.fieldCancelReasonApi.setVisible(isAppointmentRequest);
    this.fieldCancelReasonListApi.setVisible(!isAppointmentRequest);

    if (isAppointmentRequest) {
      this.fieldCancelReasonApi.addValidator(requiredValidator);
    } else {
      this.fieldCancelReasonListApi.addValidator(requiredValidator);
      this.featchCancelReasonListApi.setParameters({ ieScheduleType: this.context.get('scheduleType') });
      this.featchCancelReasonListApi.forceFetch();
    }
  }

  @OnChange({ target: PseudonymAppointmentsCancelAliases.FORM_CANCEL })
  async handleOnChangeFormCancel(): Promise<void> {
    const isShowButton = await this.fieldCancelReasonListApi.validate() && await this.fieldCancelReasonApi.validate();

    if (this.buttonConfirmCancellationApi.isEnabled() !== isShowButton) {
      this.buttonConfirmCancellationApi.setEnabled(isShowButton);
    }
  }

  @OnClick({ target: PseudonymAppointmentsCancelAliases.BUTTON_CONFIRM_CANCELLATION })
  async onConfirmCancelClick(): Promise<void> {
    const isValid = await this.fieldCancelReasonListApi.validate() && await this.fieldCancelReasonApi.validate();
    if (!isValid) {
      return;
    }

    const parameters = {
      ieScheduleType: this.context.get('scheduleType'),
      ieStatus: this.context.get('status'),
      nrSeqAgenda: this.context.get('scheduleSequence'),
      patientId: this.context.get('patientId'),
    } as Record<string, Value>;
    const cancelForm = this.formCancelApi.getRecord() as unknown as AppointmentCancelForm;
    const body = {
      cdMotivo: cancelForm.reason?.cdMotivo,
      dsMotivo: cancelForm.reason?.dsMotivo,
      reasonCancellation: cancelForm.reasonCancellation
    } as Record<string, Value>;

    this.panelFormApi.setVisible(false);
    this.panelActionApi.setVisible(false);
    this.panelLoaderApi.setVisible(true);
    HttpService.post(APPLICATION_ENDPOINTS.CANCEL_AGENDA, parameters, { body })
      .finally(() => this.submittableFragmentContext.submit(new SubmitEvent()));
  }

}
